import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'MyTypes';
import { compact, isEmpty, omit, omitBy, uniq } from 'lodash/fp';

import {
    areCompatibleDataTypes,
    getDataTypePrimaryEntityKey,
    sortDataTypes,
} from '@portal/common/models/data-type';
import { OPERATION_STATUS } from '@portal/common/models/operation-status';
import {
    AiChatDataExplorerState,
    AiChatFilterSet,
    DataCondition,
    DataFilters,
    DataGranularityKey,
} from '@portal/common/types';
import {
    getFiltersDiff,
    getManualFiltersChips,
    getThreadManualFiltersChips,
    getUncomparedFilterKeys,
} from '@portal/common/utility/ai-chat-manual-filters';

import { conditionsToDataFilters } from '../../utility/data-loader';
import {
    getDataToolDataCollections,
    getQueriedConditions,
    getQueriedConditionsRefinedGranularity,
    getQueriedRefinementFilters,
    getSelectedChartType,
} from '../data-explorer/selectors';
import { getSelectedDataCollectionForDataTool } from '../user-settings/selectors';
import {
    getLastFilterBearingMessage,
    isChatSupportedDataCollection,
    isFilterBearingMessage,
    isManualFiltersMessage,
    isOpenDataCollectionPickerMessage,
} from './utils';

export const getChatMessages = (state: RootState) => state.chat.messages;
export const getHasChatMessages = (state: RootState) => !isEmpty(state.chat.messages);
export const getChatRequestStatus = (state: RootState) => state.chat.requestStatus;
export const getChatId = (state: RootState) => state.chat.chatId;
export const getChatDataCollectionId = (state: RootState) => state.chat.dataCollectionId;
export const getChatLastPickedDataCollectionId = (state: RootState) =>
    state.chat.lastPickedDataCollectionId;
export const getFeedbackSubmission = (state: RootState) => state.chat.feedbackSubmission;
export const getIsManualFiltersSyncEnabled = (state: RootState) =>
    state.chat.isManualFiltersSyncEnabled;
const getIsAwaitingMirror = (state: RootState) => state.chat.isAwaitingMirror;

const getChatAppliedFilterSet = createSelector(getChatMessages, getLastFilterBearingMessage);

export const getMirroredMessageId = createSelector(
    getChatAppliedFilterSet,
    (message) => message?.id ?? null
);

const toFilterSet = (
    conditions: DataCondition[],
    refinementFilters: DataFilters | null
): AiChatFilterSet | null => {
    if (isEmpty(conditions)) {
        return null;
    }

    const dataTypes = sortDataTypes(conditions.map((condition) => condition.data_type));
    const entityKeys = compact(dataTypes.map(getDataTypePrimaryEntityKey));
    const filters: DataFilters = {
        ...omit(entityKeys, refinementFilters ?? {}),
        ...conditionsToDataFilters(conditions),
    };

    return { dataTypes, filters };
};

// Data Explorer queries a filter left out, e.g. years when all are selected, with all its values.
const withAllValuesWhenMissing = (
    filters: DataFilters,
    keys: DataGranularityKey[],
    granularity: DataFilters
): DataFilters =>
    Object.fromEntries(
        keys.map((key) => [key, isEmpty(filters[key]) ? granularity[key] : filters[key]])
    );

const getQueriedFilterSet = createSelector(
    getQueriedConditions,
    getQueriedRefinementFilters,
    toFilterSet
);

const getQueriedDataExplorerState = createSelector(
    getSelectedDataCollectionForDataTool,
    getQueriedFilterSet,
    getSelectedChartType,
    (dataCollection, filterSet, chartType): AiChatDataExplorerState | null =>
        dataCollection && filterSet
            ? { ...filterSet, dataCollectionId: dataCollection.id, visualization: chartType }
            : null
);

const getComparedFilters = createSelector(
    getQueriedFilterSet,
    getChatAppliedFilterSet,
    getQueriedConditionsRefinedGranularity,
    (current, applied, granularity) => {
        // Switching data type resets the chat, so a mismatch is only a mirror in progress.
        if (!current || !applied || !areCompatibleDataTypes(applied.dataTypes, current.dataTypes)) {
            return null;
        }

        const keys = uniq([
            ...Object.keys(current.filters),
            ...Object.keys(applied.filters),
        ]) as DataGranularityKey[];

        return {
            current: withAllValuesWhenMissing(current.filters, keys, granularity ?? {}),
            applied: withAllValuesWhenMissing(applied.filters, keys, granularity ?? {}),
            uncomparedKeys: getUncomparedFilterKeys(applied),
        };
    }
);

const getManualFiltersChanges = createSelector(
    getComparedFilters,
    getIsAwaitingMirror,
    (compared, isAwaitingMirror): DataFilters | null => {
        if (!compared || isAwaitingMirror) {
            return null;
        }

        const changes = getFiltersDiff(compared.current, compared.applied, compared.uncomparedKeys);

        return isEmpty(changes) ? null : changes;
    }
);

// The whole queried state, as the chat stores it as its latest applied filters.
export const getManualFiltersToShare = createSelector(
    getIsManualFiltersSyncEnabled,
    getQueriedDataExplorerState,
    getComparedFilters,
    getManualFiltersChanges,
    (isEnabled, queried, compared, changes): AiChatDataExplorerState | null =>
        isEnabled && queried && compared && changes
            ? { ...queried, filters: omitBy(isEmpty, compared.current) as DataFilters }
            : null
);

export const getPendingManualFilters = createSelector(
    getManualFiltersToShare,
    getManualFiltersChanges,
    getChatId,
    getChatRequestStatus,
    (toShare, changes, chatId, requestStatus): AiChatFilterSet | null => {
        if (!toShare || !chatId || requestStatus === OPERATION_STATUS.PENDING) {
            return null;
        }

        return {
            dataTypes: toShare.dataTypes,
            filters: getManualFiltersChips(toShare.filters, changes),
        };
    }
);

// Built from the thread only, so past chips don't change with Data Explorer.
export const getManualFiltersChipsByMessage = createSelector(getChatMessages, (messages) =>
    getThreadManualFiltersChips(
        messages,
        (message) => (isFilterBearingMessage(message) ? message : null),
        isManualFiltersMessage
    )
);

export const getIsChatDataCollectionSelectorVisible = createSelector(
    getDataToolDataCollections,
    (dataCollections) => (dataCollections ?? []).filter(isChatSupportedDataCollection).length > 1
);

export const getHasOpenChatDataCollectionPicker = createSelector(getChatMessages, (messages) =>
    messages.some(isOpenDataCollectionPickerMessage)
);

export const getIsChatAutoDataCollection = (state: RootState) =>
    state.chat.isAutoDataCollection && getIsChatDataCollectionSelectorVisible(state);
